import { useId, useMemo, useState } from 'react'
import { ChevronDownIcon } from '@/components/Icons'
import {
  COMBOBOX_CHEVRON_CLASS,
  COMBOBOX_LABEL_CLASS,
  COMBOBOX_LIST_CLASS,
  COMBOBOX_NOTICE_CLASS,
  COMBOBOX_PANEL_CLASS,
  COMBOBOX_PLACEHOLDER_CLASS,
  COMBOBOX_SEARCH_CLASS,
  COMBOBOX_SEARCH_WRAP_CLASS,
  COMBOBOX_TRIGGER_CLASS,
  COMBOBOX_TRIGGER_COMPACT_CLASS,
  COMBOBOX_VALUE_CLASS,
} from '@/components/comboboxStyles'
import { useComboboxPanel } from '@/hooks/useComboboxPanel'

export interface ComboboxOption {
  value: string
  /** What the row and the closed control read. */
  label: string
  /** Extra text the search also matches, without being shown separately. */
  keywords?: string
}

export interface ComboboxProps {
  label: string
  /** `''` means nothing is chosen, and shows `placeholder` instead. */
  value: string
  onChange: (value: string) => void
  options: ComboboxOption[]
  placeholder: string
  searchLabel: string
  emptyLabel: string
  id?: string
  disabled?: boolean
  /**
   * Inline beside a heading rather than stacked in a form. The control
   * shrinks to its content, and its label is left to screen readers — the
   * heading it sits against already says what is being chosen.
   */
  compact?: boolean
}

/**
 * A single-choice dropdown with a search box, wearing the same face as
 * {@link SelectField}.
 *
 * For the lists a native `select` stops serving: scrolling 78 projects for the
 * one whose code you already know is slower than typing three letters of it.
 * Everything about *where* the panel goes — and why it is `position: fixed` —
 * lives in {@link useComboboxPanel}, which `ShareWithMembersField` shares, so
 * the two panels cannot drift apart.
 *
 * The search box is always shown rather than appearing past some number of
 * options: a control that changes shape depending on how much data came back
 * is one people have to look at twice.
 */
export default function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  searchLabel,
  emptyLabel,
  id,
  disabled = false,
  compact = false,
}: ComboboxProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const labelId = useId()
  const listboxId = useId()
  const [search, setSearch] = useState('')

  // Filtered here rather than on the server: the whole list arrives in one
  // request and is small enough that a round trip per keystroke would only add
  // latency to an answer already in memory.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) =>
      `${option.label} ${option.keywords ?? ''}`.toLowerCase().includes(needle)
    )
  }, [options, search])

  const panel = useComboboxPanel(visible.length)
  const selected = options.find((option) => option.value === value)

  function choose(next: string) {
    onChange(next)
    // Closes, unlike the multi-choice panel: there is nothing else to pick.
    panel.setOpen(false)
    setSearch('')
    panel.triggerRef.current?.focus()
  }

  function onSearchKeyDown(event: React.KeyboardEvent) {
    if (panel.handleNavigationKey(event)) return
    if (event.key === 'Enter') {
      event.preventDefault()
      const option = visible[panel.activeIndex]
      if (option) choose(option.value)
    }
  }

  return (
    <div ref={panel.rootRef}>
      {/* Dropped rather than hidden with `sr-only`, which is
          `position: absolute`: with no positioned ancestor its containing
          block is the page, so a hidden label deep inside a scrolling list
          is not clipped by that list — it sits at its own document
          coordinate and makes the page that tall. One in the Next actions
          header put ~940px of blank under the app (BRAI-678 review). The
          name moves to `aria-label`, which costs nothing and occupies no
          box at all. */}
      {compact ? null : (
        <p id={labelId} className={COMBOBOX_LABEL_CLASS}>
          {label}
        </p>
      )}

      <button
        id={fieldId}
        ref={panel.triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={panel.open}
        aria-controls={panel.open ? listboxId : undefined}
        aria-label={compact ? label : undefined}
        aria-labelledby={compact ? undefined : labelId}
        disabled={disabled}
        onClick={() => panel.setOpen(!panel.open)}
        className={compact ? COMBOBOX_TRIGGER_COMPACT_CLASS : COMBOBOX_TRIGGER_CLASS}
      >
        <span
          className={[COMBOBOX_VALUE_CLASS, selected ? '' : COMBOBOX_PLACEHOLDER_CLASS].join(' ')}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDownIcon className={COMBOBOX_CHEVRON_CLASS} aria-hidden />
      </button>

      {panel.open && (
        <div
          style={panel.panelStyle}
          className={COMBOBOX_PANEL_CLASS}
        >
          <div className={COMBOBOX_SEARCH_WRAP_CLASS}>
            <input
              ref={panel.searchRef}
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={onSearchKeyDown}
              aria-label={searchLabel}
              placeholder={searchLabel}
              className={COMBOBOX_SEARCH_CLASS}
            />
          </div>

          <ul
            id={listboxId}
            role="listbox"
            aria-label={label}
            className={COMBOBOX_LIST_CLASS}
          >
            {visible.length === 0 ? (
              <li className={COMBOBOX_NOTICE_CLASS}>
                {emptyLabel}
              </li>
            ) : (
              visible.map((option, index) => {
                const active = index === panel.activeIndex
                const chosen = option.value === value
                return (
                  <li key={option.value} role="presentation">
                    <button
                      ref={active ? panel.activeRowRef : undefined}
                      type="button"
                      role="option"
                      aria-selected={chosen}
                      onMouseEnter={() => panel.setActiveIndex(index)}
                      onClick={() => choose(option.value)}
                      className={[
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        active ? 'bg-[var(--bg-tertiary)]' : '',
                        chosen ? 'font-semibold text-[var(--accent)]' : '',
                      ].join(' ')}
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
